import { useState } from "react";
import { toast } from "sonner";
import { Star, EyeOff, Send } from "lucide-react";
import api, { fmtErr } from "@/lib/api";
import { useFetch, PageHeader, Loading, EmptyState, ErrorState, SectionCard, StatusChip, fmtDate } from "@/lib/ui";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

function ReviewsTab() {
  const { data, loading, error, refetch } = useFetch("/admin/reviews", []);
  const hide = async (r) => { try { await api.post(`/admin/reviews/${r.id}/hide`); toast.success("Review hidden"); refetch(); } catch (e) { toast.error(fmtErr(e)); } };
  return loading ? <Loading /> : error ? <ErrorState error={error} onRetry={refetch} /> :
    !data?.length ? <SectionCard className="p-2"><EmptyState label="No reviews" /></SectionCard> : (
      <div className="grid gap-3 md:grid-cols-2">
        {data.map((r) => (
          <SectionCard key={r.id} className="p-4" data-testid={`review-${r.id}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`h-4 w-4 ${i < r.rating ? "fill-amber-400 text-amber-400" : "text-muted"}`} />)}</div>
              {r.is_hidden ? <StatusChip status="HIDDEN" tone="gray" /> :
                <Button size="sm" variant="ghost" onClick={() => hide(r)} data-testid={`review-hide-${r.id}`}><EyeOff className="h-3.5 w-3.5 mr-1.5" /> Hide</Button>}
            </div>
            {r.comment && <p className="text-sm mt-2">{r.comment}</p>}
            <div className="text-xs text-muted-foreground mt-2">{r.customer_name || "Customer"} · {fmtDate(r.created_at)}</div>
          </SectionCard>
        ))}
      </div>
    );
}

function SupportTab() {
  const { data, loading, error, refetch } = useFetch("/admin/support/tickets", []);
  const [active, setActive] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const reply = async (close) => {
    if (!msg.trim() && !close) return;
    setBusy(true);
    try { await api.post(`/support/tickets/${active.id}/reply`, { message: msg || "Closed by admin", close }); toast.success(close ? "Ticket closed" : "Reply sent"); setActive(null); setMsg(""); refetch(); }
    catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };
  return loading ? <Loading /> : error ? <ErrorState error={error} onRetry={refetch} /> :
    !data?.length ? <SectionCard className="p-2"><EmptyState label="No support tickets" /></SectionCard> : (
      <>
        <SectionCard className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="support-table">
              <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                <tr><th className="px-4 py-2.5 font-semibold">Subject</th><th className="px-4 py-2.5 font-semibold">Messages</th><th className="px-4 py-2.5 font-semibold">Status</th><th className="px-4 py-2.5 font-semibold">Created</th><th className="px-4 py-2.5"></th></tr>
              </thead>
              <tbody className="divide-y">
                {data.map((t) => (
                  <tr key={t.id} className="hover:bg-muted/30" data-testid={`ticket-${t.id}`}>
                    <td className="px-4 py-2.5 font-medium">{t.subject}</td>
                    <td className="px-4 py-2.5 tnum">{t.messages?.length || 0}</td>
                    <td className="px-4 py-2.5"><StatusChip status={t.status} /></td>
                    <td className="px-4 py-2.5 text-muted-foreground text-xs">{fmtDate(t.created_at)}</td>
                    <td className="px-4 py-2.5 text-right"><Button size="sm" variant="outline" onClick={() => setActive(t)} data-testid={`ticket-open-${t.id}`}>Open</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>
        <Dialog open={!!active} onOpenChange={(o) => { if (!o) { setActive(null); setMsg(""); } }}>
          <DialogContent data-testid="ticket-dialog">
            <DialogHeader><DialogTitle>{active?.subject}</DialogTitle></DialogHeader>
            <div className="max-h-56 overflow-y-auto space-y-2 bg-muted/40 rounded-lg p-3">
              {active?.messages?.map((m, i) => (
                <div key={i} className="text-sm"><span className="text-xs text-muted-foreground font-mono">{fmtDate(m.at)}</span><p>{m.message}</p></div>
              ))}
            </div>
            {active?.status !== "CLOSED" && <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Type a reply…" data-testid="ticket-reply-input" />}
            <DialogFooter>
              {active?.status !== "CLOSED" && <>
                <Button variant="outline" onClick={() => reply(true)} disabled={busy} data-testid="ticket-close-btn">Reply & Close</Button>
                <Button onClick={() => reply(false)} disabled={busy} data-testid="ticket-reply-btn"><Send className="h-3.5 w-3.5 mr-1.5" /> Send</Button>
              </>}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
}

export default function Moderation() {
  return (
    <div>
      <PageHeader title="Reviews & Support" subtitle="Moderate reviews and resolve support tickets" />
      <Tabs defaultValue="reviews">
        <TabsList data-testid="moderation-tabs">
          <TabsTrigger value="reviews" data-testid="moderation-tab-reviews">Reviews</TabsTrigger>
          <TabsTrigger value="support" data-testid="moderation-tab-support">Support</TabsTrigger>
        </TabsList>
        <TabsContent value="reviews" className="mt-4"><ReviewsTab /></TabsContent>
        <TabsContent value="support" className="mt-4"><SupportTab /></TabsContent>
      </Tabs>
    </div>
  );
}
